import { forwardRef } from 'react';
import { usePopupState, bindDialog } from 'material-ui-popup-state/hooks';
import { TransitionProps } from '@mui/material/transitions';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  DialogContent,
  Stack,
  Typography,
  Dialog,
  Grow,
  IconButton,
} from '@mui/material';

import { DsgvoDialog } from '@features/auth';
import { EmailAuthForm } from '@features/auth/components/EmailAuthForm';
import { useGetSpacing } from '@ui';
import { CloseIcon } from '@ui/icons';
import { YourNiveaCremeLogo } from '@components';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Grow ref={ref} {...props} />;
});

/**
 * Staff login page – email-only authentication (no guest login, no venue).
 * Accessible at /auth/staff for unauthenticated users.
 * After successful magic link click, the token route detects isStaff and
 * redirects to /staff.
 */
const Index = () => {
  const getSpacing = useGetSpacing();
  const { t } = useTranslation();

  const emailLoginSuccessPopupState = usePopupState({
    variant: 'dialog',
    popupId: 'staffEmailLoginSuccessDialog',
  });

  const handleEmailLoginSuccess = () => {
    emailLoginSuccessPopupState.open();
  };

  return (
    <>
      <DsgvoDialog />
      <Dialog
        {...bindDialog(emailLoginSuccessPopupState)}
        slots={{
          transition: Transition,
        }}
      >
        <IconButton
          aria-label="close"
          sx={() => ({
            position: 'absolute',
            right: 12,
            top: 12,
          })}
          onClick={() => emailLoginSuccessPopupState.close()}
          color="primary"
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            paddingTop: getSpacing('xl'),
            paddingRight: getSpacing('xl'),
            paddingBottom: getSpacing('xxl'),
            paddingLeft: getSpacing('xl'),
          }}
        >
          <Stack spacing={4} alignItems={'center'}>
            <Box sx={{ width: 50, height: 50 }}>
              <img src="/nivea_house_logo.svg" alt="logo" />
            </Box>
            <Typography variant="h6">
              {t('loginEmailSendSuccessTitle')}
            </Typography>
            <Typography variant="h1">👍</Typography>
            <Typography textAlign={'center'}>
              <Trans i18nKey="loginEmailSendSuccessText" />
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!emailLoginSuccessPopupState.isOpen}
        scroll={'body'}
        fullWidth={true}
        slots={{
          transition: Transition,
        }}
      >
        <DialogContent
          sx={{
            paddingTop: getSpacing('xl'),
            paddingRight: getSpacing('xl'),
            paddingBottom: getSpacing('xxl'),
            paddingLeft: getSpacing('xl'),
          }}
        >
          <Stack
            spacing={getSpacing('l')}
            sx={{ width: '100%', maxWidth: '100%' }}
          >
            <YourNiveaCremeLogo />
            <Typography sx={{ fontSize: 18, fontWeight: 400 }}>
              {t('signInRegisterViaEmail')}
            </Typography>
            <EmailAuthForm emailLoginSuccess={handleEmailLoginSuccess} />
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const Route = createFileRoute('/_auth/auth/staff/')({
  component: Index,
});
